import { useQuery } from "@apollo/client/react";
import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Divider } from "@repo/components/src/ui/divider";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Link } from "@repo/components/src/ui/link";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import {
  formatPercentage,
  formatSeparators,
} from "@repo/components/src/utils/numbers";
import { TopPostsDocument, TopPostsQuery } from "@repo/graphql/documents/socialNetworkUser/query.generated";
import {
  Bookmark,
  Heart,
  InfoIcon,
  LucideIcon,
  MessageCircle,
  Send,
} from "lucide-react-native";
import React from "react";
import { SocialNetworkUserFragmentFragment } from "@repo/graphql/documents/socialNetworkUser/fragment.generated";

type Props = {
  instagram: SocialNetworkUserFragmentFragment;
  postulationId?: string;
  brandId?: string;
};

type Stat = {
  key: string;
  value: string;
  icon: LucideIcon;
};

const postStats = (
  post: TopPostsQuery["socialNetworkUser"]["topPosts"][number],
): Stat[] => {
  return [
    { key: "likes", icon: Heart, value: formatSeparators(post.likes) },
    {
      key: "comments",
      icon: MessageCircle,
      value: formatSeparators(post.comments),
    },
    { key: "shares", icon: Send, value: formatSeparators(post.shares) },
    { key: "bookmmark", icon: Bookmark, value: formatSeparators(post.saved) },
  ];
};

const TopPost = React.memo((props: Props) => {
  const { instagram, postulationId, brandId } = props;
  const topPostsQuery = useQuery(TopPostsDocument, {
    variables: {
      socialNetworkUserId: instagram.id,
      brandId: brandId,
      postulationId: postulationId,
    },
  });
  const isLoading = topPostsQuery.loading;
  return (
    <>
      <Heading className="font-medium flex-1 shrink" numberOfLines={1}>
        Top posts en Instagram
      </Heading>
      <HStack flexSpace="md" className="mt-3">
        {isLoading
          ? Array.from({ length: 6 }).map((_, index) => {
              return (
                <Box className="w-1/3" key={`loading-${index}`}>
                  <Skeleton
                    variant="rounded"
                    className="aspect-square w-full"
                  />
                </Box>
              );
            })
          : topPostsQuery.data?.socialNetworkUser.topPosts.map(
              (postFragment) => {
                const post = postFragment;
                const media = post.thumbnail;
                return (
                  <Box className="w-1/3" key={post.id}>
                    <Box className="p-2 shadow-md rounded-lg h-full">
                      <Link href={post.link} isExternal>
                        <Image
                          source={{ uri: media?.url }}
                          alt={post.caption}
                          size="none"
                          className="w-full aspect-square rounded-lg"
                          contentFit="cover"
                          contentPosition="center"
                        />
                      </Link>
                      <Text
                        size="sm"
                        className="my-3"
                        numberOfLines={2}
                        action="muted"
                      >
                        {post.caption}
                      </Text>
                      <HStack className="items-center" space="md">
                        {postStats(post).map((stat) => {
                          return (
                            <HStack
                              className="items-center"
                              space="2xs"
                              key={stat.key}
                            >
                              <Icon as={stat.icon} size="sm" action="brand" />
                              <Text action="muted" size="sm">
                                {stat.value}
                              </Text>
                            </HStack>
                          );
                        })}
                      </HStack>
                      <Card
                        variant="filled"
                        className="border-none mt-3"
                        size="sm"
                      >
                        <HStack space="md">
                          <Box>
                            <Text size="xs" action="muted">
                              Alcance
                            </Text>
                            <Text size="xs">
                              {formatSeparators(post.reach)}
                            </Text>
                          </Box>
                          <Divider orientation="vertical" />
                          <Box>
                            <Text size="xs" action="muted">
                              Views
                            </Text>
                            <Text size="xs">
                              {formatSeparators(post.views)}
                            </Text>
                          </Box>
                          <Divider orientation="vertical" />
                          <Box>
                            <HStack space="xs" className="items-center">
                              <Text size="xs" action="muted">
                                Engagement
                              </Text>
                              <Tooltip
                                placement="right"
                                trigger={(triggerProps) => (
                                  <Box {...triggerProps}>
                                    <Icon
                                      as={InfoIcon}
                                      action="muted"
                                      size="xs"
                                    />
                                  </Box>
                                )}
                              >
                                <TooltipContent arrow="left" className="w-48">
                                  <TooltipText>
                                    Porcentaje de interacciones que recibe en
                                    relación a su número de seguidores.
                                  </TooltipText>
                                </TooltipContent>
                              </Tooltip>
                            </HStack>
                            <Text size="xs">
                              {formatPercentage(
                                post.reach
                                  ? post.totalInteractions / post.reach
                                  : 0,
                              )}
                            </Text>
                          </Box>
                        </HStack>
                      </Card>
                    </Box>
                  </Box>
                );
              },
            )}
      </HStack>
    </>
  );
});

TopPost.displayName = "TopPost";

export default TopPost;
